///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Resolves the persisted "recently used" snapshots back into runnable
/// commands. Live commands (actions, saved views, placemarks) resolve
/// against their current lists so titles stay fresh and deleted items
/// drop out; database entities (places, satellites, ...) carry their
/// entity id in the command id and are rebuilt so focus can be
/// re-dispatched without the original search result.
///

import { useSavedViewCommands } from "@/components/controls/layers-panel/useSavedViewCommands"
import { usePlacemarkCommands } from "@/components/controls/layers-panel/usePlacemarkCommands"
import { useUnifiedCommands } from "@/components/controls/layers-panel/useUnifiedCommands"
import type { CommandItem } from "@/components/controls/layers-panel/commandTypes"
import { sendDatasetAction } from "@/redux/earthBrowserSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { recordRecentCommand, type RecentCommandEntry, type RecentCommandType } from "@/redux/userInterfaceSlice"
import type { DatasetType } from "@/types/DatasetElement"
import { pushTargetToHistory } from "@/utils/navigationHistory"
import { Activity, Landmark, Mountain, Satellite } from "lucide-react"
import { useCallback, useMemo } from "react"
import type { ReactNode } from "react"

// Database entity types that persisted recents can resurrect: dataset for
// the selectId dispatch, target prefix for the URL, and the display icon
// (matching useGlobalSearchCommands)
const RECENT_ENTITY_CONFIG: Partial<Record<RecentCommandType, { dataset: DatasetType; targetPrefix: string; icon: () => ReactNode }>> = {
	location: { dataset: "location", targetPrefix: "geoid", icon: () => <Landmark className="w-4 h-4 text-green-400" /> },
	satellite: { dataset: "satellite", targetPrefix: "sat", icon: () => <Satellite className="w-4 h-4 text-blue-400" /> },
	earthquake: { dataset: "earthquake", targetPrefix: "quake", icon: () => <Activity className="w-4 h-4 text-red-500" /> },
	volcano: { dataset: "volcano", targetPrefix: "volcano", icon: () => <Mountain className="w-4 h-4 text-orange-500" /> },
}

function rebuildEntityCommand(entry: RecentCommandEntry, run: (dataset: DatasetType, targetPrefix: string, entityId: string) => void): CommandItem | null {
	const config = RECENT_ENTITY_CONFIG[entry.type]
	if (!config) return null
	const entityId = entry.id.slice(entry.type.length + 1)
	if (!entityId) return null
	return {
		id: entry.id,
		type: entry.type,
		title: entry.title,
		subtitle: entry.subtitle,
		icon: config.icon(),
		action: () => run(config.dataset, config.targetPrefix, entityId),
	}
}

export function useRecentItems(): { items: CommandItem[]; run: (cmd: CommandItem) => void } {
	const dispatch = useAppDispatch()
	const recentCommands = useAppSelector(state => state.userInterface.recentCommands)
	const staticCommands = useUnifiedCommands()
	const savedViewCommands = useSavedViewCommands()
	const placemarkCommands = usePlacemarkCommands()

	const runRecentEntity = useCallback((dataset: DatasetType, targetPrefix: string, entityId: string) => {
		dispatch(sendDatasetAction({ type: dataset, action: "selectId", id: entityId }))
		pushTargetToHistory(targetPrefix, entityId)
	}, [dispatch])

	const items = useMemo((): CommandItem[] => {
		return recentCommands
			.map(entry => {
				const live = staticCommands.find(c => c.id === entry.id)
					?? savedViewCommands.find(c => c.id === entry.id)
					?? placemarkCommands.find(c => c.id === entry.id)
				if (live) return live.enabled === false ? null : live
				return rebuildEntityCommand(entry, runRecentEntity)
			})
			.filter((cmd): cmd is CommandItem => cmd !== null)
	}, [recentCommands, staticCommands, savedViewCommands, placemarkCommands, runRecentEntity])

	// Re-running bumps the entry back to the top of the list
	const run = useCallback((cmd: CommandItem) => {
		dispatch(recordRecentCommand({
			id: cmd.id,
			type: cmd.type,
			title: cmd.title,
			subtitle: cmd.subtitle,
		}))
		cmd.action()
	}, [dispatch])

	return { items, run }
}
